import * as React from 'react';
import { FormattedNumberParts } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import classnames from 'classnames';
import styled from 'styled-components';
import Values from 'values.js';

import { IContract } from 'src/types/IContract';
import {
  useContractContext,
  useFeatures,
  useLocale,
  useModalSettingsContext,
  usePostMessageContext,
  useTheme,
  useStrings,
} from 'src/providers';
import { useCurrencyFormatter } from 'src/hooks/useCurrencyFormatter';
import { useEffectOnce } from 'src/hooks/useEffectOnce';

import { FormattedString } from '../Shared/FormattedString';

interface IColors {
  colors: {
    merchantColor: string;
    monthlyPriceBackground: string;
  };
}

const StyledWrapper = styled.div<IColors>`
  display: flex;
  align-items: center;
  justify-content: center;

  .contract {
    position: relative;
    display: inline-block;
    width: 100%;
    max-width: 40%;
    box-sizing: border-box;

    & input {
      opacity: 0;
      position: absolute;
    }

    padding: 16px;
    @media (max-width: 550px) {
      padding-left: 10px;
    }

    border: 1px solid #d1d1d1;
    border-radius: 8px;
    text-decoration: none;
    cursor: pointer;
    color: var(--black);
    background-color: var(--white);
    transition-timing-function: ease;
    transition-duration: 200ms;
    transition-property: border-color, box-shadow;

    margin-left: 8px;
    @media (min-width: 600px) {
      margin-left: 16px;
    }
    &:first-child {
      margin-left: 0;
    }

    &.selected {
      padding: 15px;
      @media (max-width: 550px) {
        padding-left: 9px;
      }
      border-width: 2px;
      border-color: ${(props) => props.colors.merchantColor};
      outline: none;
    }

    &:hover:not(.selected) {
      border-color: ${(props) => props.colors.merchantColor};

      .contract__popular-badge__badge {
        color: #ffffff;
        background-color: ${(props) => props.colors.merchantColor};
      }

      .contract__value-badge__badge {
        color: #ffffff;
        background-color: ${(props) => props.colors.merchantColor};

        svg path {
          fill: #ffffff;
        }
      }
    }


    &:active {
      box-shadow: none;
      outline: none;
    }

    &-most-popular {
      border-radius: 0 0 8px 8px;
    }

    &-best-value {
      border-radius: 0 0 8px 8px;
    }

    &-top-spaced {
      margin-top: 20px;
    }
  }

  .contract__popular-svg {
    display: block;
    position: absolute;
    top: -1px;
    right: 10px;
    color: var(--light-gray);
  }

  .contract.selected .contract__popular-svg {
    color: inherit;
  }

  .contract__montly-price__container {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    width: min-content;
    flex-direction: column;
    justify-content: center;
    height: 20px;
    padding: 0.4em;
    border-radius: 100px;
    text-align: center;
    font-size: 13px;
    color: var(--black);
    background: ${(props) => props.colors.monthlyPriceBackground};
  }

  .more-space {
    bottom: -33px;
  }

  .contract__protectionplan {
    font-size: 13px;
    line-height: 1.2em;
    font-weight: 500;
  }
`;

const ContractYear = styled.span`
  font-size: 19px;
  font-weight: 500;
  line-height: 1.6em;
  margin: 0;

  @media (min-width: 600px) {
    font-size: 22px;
  }

  @media (max-width: 366px) {
    font-size: 16px;
  }
`;

const ContractPrice = styled.div`
  display: flex;
  font-size: 18px;
  line-height: 1.25em;
  margin-top: 16px;
  font-weight: 600;

  @media (min-width: 600px) {
    font-size: 24px;
  }

  @media (max-width: 660px) {
    margin-top: 16px;
  }

  @media (max-width: 479px) {
    font-size: 18px;
  }

  .contract__price__currency,
  .contract__price__integer {
    display: inline-block;
  }

  .contract__price__currency.leading {
    // Separated out b/c we only want the margin when the currency symbol is first
    margin-right: 2px;
  }

  .contract__price__decimal,
  .contract__price__fraction {
    display: inline-block;
    font-weight: 600;
    font-size: 10px;
    margin-top: -4px;

    @media (min-width: 600px) {
      font-size: 11px;
      margin-top: -6px;
    }
  }
`;

const PopularBadge = styled.div<{ isActive: boolean }>`
  position: absolute;
  right: -${({ isActive }) => (isActive ? 2 : 1)}px;
  top: -16px;
  left: -${({ isActive }) => (isActive ? 2 : 1)}px;
  height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  border-radius: 6px 6px 0 0;
  color: ${({ isActive }) => (isActive ? '#ffffff' : '#000000')};
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.merchantColor : 'var(--light-gray)'};
  font-size: 11px;
  -webkit-transition: all 200ms ease;
  transition: all 200ms ease;

  span {
    margin-left: 5px;
    padding-top: 1px;
  }

  svg {
    margin-bottom: 2px;
  }
`;

const ValueBadge = styled.div<{ isActive: boolean }>`
  position: absolute;
  right: -${({ isActive }) => (isActive ? 2 : 1)}px;
  top: -16px;
  left: -${({ isActive }) => (isActive ? 2 : 1)}px;
  height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  border-radius: 6px 6px 0 0;
  color: ${({ isActive }) => (isActive ? '#ffffff' : '#000000')};
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.merchantColor : 'var(--light-gray)'};
  font-size: 11px;
  -webkit-transition: all 200ms ease;
  transition: all 200ms ease;

  span {
    margin-left: 5px;
    padding-top: 1px;
  }

  svg {
    margin-bottom: 2px;
  }

  svg path {
    fill: ${({ isActive }) => (isActive ? '#ffffff' : '#000000')};
  }
`;

const ContractSelector = () => {
  const { contracts, selectContract, selectedContractId } = useContractContext();
  const { monthlyPrice, mostPopularBadge, bestValueBadge } = useFeatures();
  const { postMessage } = usePostMessageContext();
  const { merchantColor } = useTheme();
  const formatCurrency = useCurrencyFormatter();
  const isMobile = useMediaQuery({ query: '(max-width: 600px)' });
  const { modalType } = useModalSettingsContext();
  const { bestValueLabel, mostPopularLabel, termLabel } = useStrings();
  const { currency } = useLocale();

  // IMPORTANT: we don't want to fire multiple events for monthly pricing
  useEffectOnce(() => {
    if (monthlyPrice) {
      postMessage('showMonthlyPriceAppended', {
        ctaType: 'modal',
        modalType,
      });
    }
  });

  const colors = React.useMemo(() => ({
    merchantColor,
    monthlyPriceBackground: new Values(merchantColor).tint(90).hexString(),
  }), [merchantColor]);

  const hasOnlyOneContract = React.useMemo(() => {
    return contracts.length === 1;
  }, [contracts]);

  const maybeRenderMonthlyPrice = React.useCallback((contract: IContract) => {
    if (!monthlyPrice || !contract.price) {
      return null;
    }

    const { price, term, termMonths } = contract;
    const monthsInATerm = typeof termMonths === 'number' ? termMonths : term * 12;
    const parsedPrice = parseFloat(price);
    const pricePerMonth = formatCurrency(parsedPrice / monthsInATerm);

    return (
      <div className="contract__montly-price__container">
        {pricePerMonth}
        {isMobile ? '/mo.' : '/month'}
      </div>
    );
  }, [formatCurrency]);

  const lowestCostContractIndex = React.useMemo(() => {
    if (hasOnlyOneContract) {
      return null;
    }
    let bestValueIndex = 0;
    let curBestValue = 0;
    const monthlyPriceSet = new Set();

    for (let i = 0; i < contracts.length; i++) {
      const {
        price,
        term,
        termMonths,
      } = contracts[i];
      
      const getTermInMonths = termMonths ? termMonths : term * 12;
      const costPerMonth = (+price / getTermInMonths);
      monthlyPriceSet.add(costPerMonth);
      if (costPerMonth < curBestValue) {
        bestValueIndex = i;
      }

      curBestValue = curBestValue
        ? Math.min(curBestValue, costPerMonth)
        : costPerMonth;
    }
    
    // Edge case in which all contracts have the same monthly cost.  
    // Return longest term index
    if (monthlyPriceSet.size !== contracts.length) {
      return contracts.length - 1;
    }

    return bestValueIndex;
  }, [contracts]);

  const maybeRenderBestValueBadge = (contract: IContract) => {
    if (!bestValueBadge || hasOnlyOneContract) {
      return null;
    }
    const activeTile = selectedContractId === contract.id;
    const valueClass = classnames('contract__value-badge__badge', {
      'contract__value-badge__badge-active': activeTile,
    });

    return (
      <ValueBadge isActive={activeTile} className={valueClass}>
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="11" viewBox="0 0 8 11" fill="none">
          <path
            clipRule="evenodd" d="M7.46409 2L3.99999 0L0.535889 2V6L2.04764 6.87281L1.49947 10.9999L3.99947 9.49994L6.49947 10.9999L5.95193 6.87305L7.46409 6V2Z"
            fill="currentColor"/>
        </svg>
        <span>{bestValueLabel}</span>
      </ValueBadge>
    );
  };

  const maybeRenderMostPopularBadge = (contract: IContract, index: number) => {
    if (!mostPopularBadge 
      || index !== 1
      || (bestValueBadge && lowestCostContractIndex === 1)) {
      return null;
    }

    const activeTile = selectedContractId === contract.id;
    const popularClass = classnames('contract__popular-badge__badge', {
      'contract__popular-badge__badge-active': activeTile,
    });

    return (
      <PopularBadge isActive={activeTile} className={popularClass}>
        <svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
          <title>Selected Contract Icon</title>
          <path
            d="M5 0L6.545 2.96215L10 3.44006L7.5 5.74448L8.09 9L5 7.46215L1.91 9L2.5 5.74448L0 3.44006L3.455 2.96215L5 0Z"
            fill="currentColor"
          />
        </svg>
        <span>{mostPopularLabel}</span>
      </PopularBadge>
    );
  };

  const renderContractRadioButton = (contract: IContract, index: number) => {
    const {
      id,
      price = '',
      termLabelPlural,
      termLabelSingular,
    } = contract;

    const selected = selectedContractId === id;
    const className = classnames(`contract contract-${id}`, {
      selected,
      'contract-most-popular': mostPopularBadge && index === 1,
      'contract-best-value': bestValueBadge && index === lowestCostContractIndex,
      'contract-top-spaced': mostPopularBadge,
    });
    return (
      <label
        key={id}
        className={className}
        data-id={id}
        data-testid={`contract-${id}`}
        aria-label={`${termLabelSingular} protection plan for $${price.toString()}`}
      >
        <input 
          type="radio" 
          name="contract"
          checked={selectedContractId === id}
          onChange={() => selectContract(id)} 
        />
        {maybeRenderMostPopularBadge(contract, index)}
        {
          index === lowestCostContractIndex
            && maybeRenderBestValueBadge(contract)
        }
        <ContractYear>
          <FormattedString
            string={termLabel}
            tagName="fragment"
            values={{
              termPlural: termLabelPlural,
              termSingular: termLabelSingular,
            }}
          />
        </ContractYear>
        <ContractPrice>
          <FormattedNumberParts currency={currency || 'USD'} style='currency' value={+price}>
            {(parts: Intl.NumberFormatPart[]) => (
              <>
                {
                  parts.map((part: Intl.NumberFormatPart, idx: number) => (
                    <div
                      className={classnames(`contract__price__${part.type}`, {
                        leading: idx === 0 && part.type === 'currency',
                      })}
                      key={part.type}
                    >
                      {part.value}
                    </div>
                  ))
                }
              </>
            )}
          </FormattedNumberParts>
        </ContractPrice>
        {maybeRenderMonthlyPrice(contract)}
      </label>
    );
  };

  return (
    <StyledWrapper
      className="plandata-tab__options"
      colors={colors}
      data-test="modal-options"
      id="options"
    >
      {contracts.map(renderContractRadioButton)}
    </StyledWrapper>
  );
};

export default ContractSelector;
